import { isHTMLElement } from "@prismicio/editor-support/DOM"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react"
import { Suspense, useLayoutEffect, useRef, useState } from "react"

import {
  getImageLabel,
  type ImageBlockData,
  MAIN_THUMBNAIL_NAME,
} from "../../../../core/imageUtils"
import { EmptyCardContent } from "../../../ImageField/Components/EmptyCardContent"
import { ImageFieldCard } from "../../../ImageField/Components/ImageFieldCard"
import { ImageFieldDropZone } from "../../../ImageField/Components/ImageFieldDropZone"
import { NonEmptyCardContent } from "../../../ImageField/Components/NonEmptyCardContent"
import { useSelected } from "../useSelected"
import { useImageView } from "./useImageView"

export interface ImageViewProps extends NodeViewProps {
  node: ImageNode
}

export function ImageView(props: ImageViewProps) {
  const { node } = props

  const content = node.attrs.content

  const { selected } = useSelected(props)
  const cardRef = useRef<HTMLDivElement>(null)

  const thumbnail = {
    width: undefined,
    height: undefined,
    name: MAIN_THUMBNAIL_NAME,
  }

  const {
    onMediaSelected,
    onCropperDialogClose,
    isUpdating,
    updateContent,
    onClear,
    uploadImage,
    isUploadingImage,
  } = useImageView({ content, thumbnail, imageViewProps: props })

  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false)

  const fieldLabel = "Feature image"

  const isImageEmpty = content?.width === 0 && content?.height === 0

  const imageLabel =
    content && !isImageEmpty ? [`${content.width}×${content.height}px`] : []

  const image = {
    thumbnail,
    contentView: content,
    labels: getImageLabel(thumbnail, content, content),
  }

  useLayoutEffect(() => {
    const node = cardRef?.current?.closest(`.${nodeWrapperClassName}`)
    if (!isHTMLElement(node)) return

    // ImageExtension sets an initial height to the wrapper to combat layout shift.
    // Now that this React component is rendered, let it take over and revert the parent node to height=auto.
    node.style.removeProperty("min-height")
  }, [])

  const isFieldUpdating = isUploadingImage || isUpdating

  return (
    <NodeViewWrapper>
      <ImageFieldCard
        ref={cardRef}
        isMediaDialogOpen={isMediaDialogOpen}
        selected={selected}
        onMediaSelected={onMediaSelected}
        onMediaDialogOpenChange={setIsMediaDialogOpen}
      >
        <Suspense fallback={fallback}>
          {content?.url ? (
            <NonEmptyCardContent
              allContentViews={[content]}
              content={content}
              contentView={content}
              fieldLabel={fieldLabel}
              imageLabel={imageLabel}
              initialImages={[image]}
              isUpdating={isFieldUpdating}
              readOnly={false}
              thumbnailName={thumbnail.name}
              onClear={onClear}
              onContentChange={updateContent}
              onCropperDialogClose={onCropperDialogClose}
              onMediaDialogOpenChange={setIsMediaDialogOpen}
            />
          ) : (
            <ImageFieldDropZone
              uploadImage={uploadImage}
              isUploading={isUploadingImage}
              disabled={isMediaDialogOpen}
            >
              <EmptyCardContent
                fieldLabel={fieldLabel}
                isUpdating={isFieldUpdating}
                readOnly={false}
                onMediaDialogOpenChange={setIsMediaDialogOpen}
              />
            </ImageFieldDropZone>
          )}
        </Suspense>
      </ImageFieldCard>
    </NodeViewWrapper>
  )
}

interface ImageNode extends ProseMirrorNode {
  attrs: { content?: ImageBlockData }
}

export const nodeWrapperClassName = "imageViewNodeWrapper"

const nonEmptyCardHeight = 144
const emptyCardHeight = 94

export function imageViewHeight(isEmpty: boolean) {
  return (
    (isEmpty ? emptyCardHeight : nonEmptyCardHeight) +
    // ImageFieldCard padding
    12 +
    12 +
    // ImageFieldCard border
    1 +
    1
  )
}

// Reserves height in-between loading images.
const fallback = <div style={{ height: nonEmptyCardHeight }} />
